import React, { useState } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiWorker } from '../../api/worker';
import { cn } from '../../lib/utils';
import { AccessGate } from '../../components/ui/AccessGate';

export const TemplatesPage = () => {
  const { isSuperAdmin, role } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [templateName, setTemplateName] = useState('');
  const [memberTerm, setMemberTerm] = useState('Athlete');
  const [parentTerm, setParentTerm] = useState('League');
  const [childTerm, setChildTerm] = useState('Team');
  const [modules, setModules] = useState<Record<string, boolean>>({
    DIRECTORY: true,
    LEDGER: true,
    REQUIREMENTS: true,
    VAULT: false,
    CALENDAR: false,
    ELECTIONS: false
  });

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['admin', 'templates'],
    queryFn: async () => {
      const res = await apiWorker.get('/api/admin/templates');
      return Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.content) ? res.data.content : []);
    },
    enabled: isSuperAdmin
  });

  // Auto-select the first template when the list loads (render-phase adjustment).
  const firstTemplate = templates[0] ?? null;
  const [prevAutoTemplate, setPrevAutoTemplate] = useState<any>(null);
  if (firstTemplate && !selectedTemplate && firstTemplate !== prevAutoTemplate) {
    setPrevAutoTemplate(firstTemplate);
    setSelectedTemplate(firstTemplate);
  }

  // Sync the editable fields when the selected template changes (render-phase adjustment).
  const [prevSyncedTemplate, setPrevSyncedTemplate] = useState<any>(null);
  if (selectedTemplate && prevSyncedTemplate !== selectedTemplate) {
    setPrevSyncedTemplate(selectedTemplate);
    setTemplateName(selectedTemplate.name || '');
    setMemberTerm(selectedTemplate.terminology?.member || 'Athlete');
    setParentTerm(selectedTemplate.terminology?.parentNode || 'League');
    setChildTerm(selectedTemplate.terminology?.childNode || 'Team');
    
    const mods = (selectedTemplate.modules || []).map((m: string) => m.toUpperCase());
    setModules({
      DIRECTORY: mods.includes('DIRECTORY') || mods.includes('ROSTER'),
      LEDGER: mods.includes('LEDGER') || mods.includes('DUES'),
      REQUIREMENTS: mods.includes('REQUIREMENTS') || mods.includes('SCHEDULES'),
      VAULT: mods.includes('VAULT') || mods.includes('WAIVERS'),
      CALENDAR: mods.includes('CALENDAR') || mods.includes('EVENTS'),
      ELECTIONS: mods.includes('ELECTIONS')
    });
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const enabledModules = Object.keys(modules).filter(k => modules[k]);
      await apiWorker.put(`/api/admin/templates/${selectedTemplate.id}`, {
        name: templateName || selectedTemplate.name,
        terminology: {
          member: memberTerm,
          parentNode: parentTerm,
          childNode: childTerm
        },
        modules: enabledModules
      });
    },
    onSuccess: () => {
      toast.success('Template updated successfully');
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
    onError: () => toast.error('Failed to update template')
  });

  if (!isSuperAdmin) {
    return (
      <AccessGate
        reason="platform-admin"
        role={role}
        requirement="You must be a Super Admin to edit organization templates."
        onBack={() => navigate('/system-admin')}
      />
    );
  }

  const handleModuleToggle = (mod: string) => {
    setModules(prev => ({
      ...prev,
      [mod]: !prev[mod]
    }));
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <header className="flex items-center justify-between border-b pb-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/system-admin')} className="p-2"><ArrowLeft size={20} /></Button>
          <div>
            <h1 className="text-2xl font-bold">Guided Setup Templates</h1>
            <p className="text-gray-500 text-sm">Edit default terminology and modules for onboarding.</p>
          </div>
        </div>
        <Button 
          onClick={() => {
            if (selectedTemplate) saveMutation.mutate();
          }} 
          className="gap-2 bg-blue-600 hover:bg-blue-700 text-white border-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-blue-600/20"
          disabled={saveMutation.isPending || !selectedTemplate}
        >
          <Save size={16} /> Save Changes
        </Button>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-4">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-gray-400" /></div>
          ) : (
            <>
              {templates.map((t: any) => (
                <Card 
                  key={t.id} 
                  className={cn(
                    "p-4 cursor-pointer hover:border-blue-500 transition border-2 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] bg-white dark:bg-slate-900",
                    selectedTemplate?.id === t.id ? "border-blue-500 ring-2 ring-blue-500/10" : "border-black dark:border-white"
                  )}
                  onClick={() => setSelectedTemplate(t)}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold truncate text-sm text-gray-800 dark:text-gray-200" title={t.name}>{t.name}</span>
                    <Badge variant={t.status === 'ACTIVE' || !t.status ? 'green' : 'amber'}>{t.status || 'ACTIVE'}</Badge>
                  </div>
                  <p className="text-[10px] text-gray-400 font-mono">{t.id}</p>
                </Card>
              ))}
              <Button 
                variant="secondary" 
                className="w-full text-xs" 
                onClick={() => setSelectedTemplate({ 
                  id: 'template-' + Date.now(), 
                  name: 'New Custom Template', 
                  terminology: { member: 'Member', parentNode: 'Organization', childNode: 'Unit' }, 
                  modules: ['roster', 'events', 'waivers'] 
                })}
              >
                + Create Template
              </Button>
            </>
          )}
        </div>

        {selectedTemplate ? (
          <Card className="md:col-span-2 p-6 border-2 border-black dark:border-white bg-white dark:bg-slate-900 rounded-none shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF]">
            <h2 className="text-lg font-bold mb-4 text-gray-850 dark:text-gray-100">Edit Template: {templateName || selectedTemplate.name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Template Name</label>
                <input 
                  type="text" 
                  className="w-full border-2 border-black dark:border-white rounded p-2 text-sm bg-white dark:bg-slate-900 border-black dark:border-white" 
                  value={templateName} 
                  onChange={e => setTemplateName(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Member Terminology</label>
                <input 
                  type="text" 
                  className="w-full border-2 border-black dark:border-white rounded p-2 text-sm bg-white dark:bg-slate-900 border-black dark:border-white" 
                  value={memberTerm} 
                  onChange={e => setMemberTerm(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Parent Org Terminology</label>
                <input 
                  type="text" 
                  className="w-full border-2 border-black dark:border-white rounded p-2 text-sm bg-white dark:bg-slate-900 border-black dark:border-white" 
                  value={parentTerm} 
                  onChange={e => setParentTerm(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Child Org Terminology</label>
                <input 
                  type="text" 
                  className="w-full border-2 border-black dark:border-white rounded p-2 text-sm bg-white dark:bg-slate-900 border-black dark:border-white" 
                  value={childTerm} 
                  onChange={e => setChildTerm(e.target.value)}
                />
              </div>
              
              <div className="pt-4 border-t">
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Default Enabled Modules</label>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.DIRECTORY} onChange={() => handleModuleToggle('DIRECTORY')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Directory & Roster (DIRECTORY)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.LEDGER} onChange={() => handleModuleToggle('LEDGER')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Payments & Dues (LEDGER)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.REQUIREMENTS} onChange={() => handleModuleToggle('REQUIREMENTS')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Compliance & Requirements (REQUIREMENTS)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.VAULT} onChange={() => handleModuleToggle('VAULT')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Document Vault & Waivers (VAULT)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.CALENDAR} onChange={() => handleModuleToggle('CALENDAR')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Events & Scheduling (CALENDAR)
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer text-gray-700 dark:text-gray-300">
                    <input type="checkbox" checked={modules.ELECTIONS} onChange={() => handleModuleToggle('ELECTIONS')} className="rounded border-black dark:border-white text-blue-600 focus:ring-blue-500" /> 
                    Elections & Governance (ELECTIONS)
                  </label>
                </div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="md:col-span-2 p-6 flex flex-col justify-center items-center text-center text-gray-400 border-2 border-black dark:border-white border-dashed rounded-none h-80 bg-white/50">
            Please select a template to edit.
          </Card>
        )}
      </div>
    </div>
  );
};
